// =============================================================================
// ui/tabs/SidebarVisibility.tsx — Settings → Preferences → which tabs show.
// -----------------------------------------------------------------------------
// The shell has honoured `hiddenTabs` since it shipped; only the editor was
// missing. That is the Default Risk shape again — a setting that is READ and not
// writable, where the effect is visible and the cause is not.
//
// ── 🚨 EVERY HIDDEN TAB IS LISTED, NOT JUST THE ONES WE OFFER ──
//
// The old client keeps a curated `HIDEABLE_TABS` of three, and its own comment
// names the hazard: *"hiddenTabs and HIDEABLE_TABS are separate, so hiding
// without listing strands the tab with no way to bring it back."*
//
// Both clients write the same column. If the other one hides something this list
// does not offer — a tab it has and this one does not, a default that changes,
// anything — that tab is gone from this client with no control to restore it.
// So the offered set is the curated three PLUS anything currently hidden,
// whatever it is. An unknown id renders under its own name and can be unticked.
//
// ⚠ AND THE CORE TABS ARE NOT OFFERED. `visibleGroups` filters on id alone and
// would happily hide Dashboard or Settings itself — hiding Settings removes the
// only screen that could bring it back. Offering them would be handing the user
// a way to lock themselves out.
// =============================================================================

import { usePreferences } from '../state/preferences';
import { TABS } from '../shell/tabs';

/**
 * The curated list, transcribed from the old client's `HIDEABLE_TABS`.
 *
 * ⚠ These three and no more. Everything else on the sidebar is either core
 * navigation or a tab whose absence would leave a section heading over nothing.
 */
const HIDEABLE: readonly { id: string; label: string; description: string }[] = [
  {
    id: 'bestopps', label: 'Best Opps',
    description: 'Setups you saw but did not take. Hide it if you do not track missed opportunities separately.',
  },
  {
    id: 'review', label: 'Review',
    description: 'Weekly and monthly retrospectives. Hide it if you review elsewhere.',
  },
  {
    id: 'practice', label: 'Practice',
    description: 'Grades replay sessions separately from live trading. In development.',
  },
];

export function SidebarVisibility() {
  const { prefs, update } = usePreferences();
  const hidden = prefs?.hiddenTabs ?? [];

  // ⚠ THE RESCUE ROW. Anything hidden that the curated list does not cover gets
  // its own entry, so no tab can be stranded by the other client — see header.
  const known = new Set(HIDEABLE.map((t) => t.id));
  const strays = hidden.filter((id) => !known.has(id)).map((id) => ({
    id,
    label: TABS.find((t) => t.id === id)?.label ?? id,
    description: TABS.find((t) => t.id === id)
      ? 'Hidden by the current client. Untick to bring it back.'
      : 'Hidden, and this client has no such tab — untick to clear the setting.',
  }));

  const rows = [...HIDEABLE, ...strays];

  const setHidden = (id: string, visible: boolean) => {
    const next = visible ? hidden.filter((x) => x !== id) : [...new Set([...hidden, id])];
    void update({ hiddenTabs: next });
  };

  return (
    <section className="card">
      <h3>Sidebar</h3>
      <p className="muted">
        Hide tabs you do not use. Changes apply immediately — the sidebar updates
        as you tick. Core tabs cannot be hidden.
      </p>
      <div className="ctag-list">
        {rows.map((t) => {
          const visible = !hidden.includes(t.id);
          return (
            <label key={t.id} className={`ctag-row tabvis-row${visible ? ' checked' : ''}`}>
              <input
                type="checkbox"
                checked={visible}
                aria-label={`Show ${t.label}`}
                onChange={(e) => setHidden(t.id, e.target.checked)}
              />
              <span className="tabvis-text">
                <b>{t.label}</b>
                <span className="muted tabvis-desc">{t.description}</span>
              </span>
            </label>
          );
        })}
      </div>
    </section>
  );
}
